import {Box, Heading, HStack, Icon, Text} from 'platform/foundation';
import styled from 'styled-components';

import i18n from '@dms/i18n';

import {getAdvertisementPreviewFeatureGroups} from '../../../utils/getAdvertisementPreviewFeatureGroups';
import {ADVERTISEMENT_PREVIEW_SECTION_ICONS} from '../constants/ADVERTISEMENT_PREVIEW_SECTION_ICONS';
import {PREVIEW_CONTAINER_BREAKPOINTS} from '../constants/PREVIEW_CONTAINER_BREAKPOINTS';
import {PREVIEW_SECTION_IDS} from '../constants/PREVIEW_SECTION_IDS';
import {getPreviewContainerBreakpoint} from '../utils/getPreviewContainerBreakpoint';
import {SectionCard} from './PreviewSectionCard';

export function AdvertisementPreviewFeatures(props: {
  groups: ReturnType<typeof getAdvertisementPreviewFeatureGroups>;
  'data-testid': string;
}) {
  const count = props.groups.reduce((total, group) => total + group.features.length, 0);

  return (
    <SectionCard id={PREVIEW_SECTION_IDS.features} data-testid={`${props['data-testid']}-features`}>
      <SectionHeading>
        <HStack align="center" spacing={2}>
          <Icon
            value={ADVERTISEMENT_PREVIEW_SECTION_ICONS.features}
            size={5}
            color="general.accent"
            isNotFilled
          />
          <Heading size={4}>{i18n.t('entity.vehicle.labels.vehicleEquipment')}</Heading>
        </HStack>
        <FeatureCount>
          <Text size="small" color="secondary">
            {i18n.t('page.advertisement.preview.featureCount', {count})}
          </Text>
        </FeatureCount>
      </SectionHeading>
      <FeatureGrid>
        {props.groups.map((group) => (
          <FeatureGroup key={group.key}>
            <GroupHeading>
              <Heading size={4} headingLevel={3}>
                {group.label}
              </Heading>
            </GroupHeading>
            <FeatureList>
              {group.features.map((feature) => (
                <HStack key={feature.value} align="flex-start" spacing={2} minWidth={0}>
                  <Box flexShrink={0}>
                    <Icon value="navigation/check" size={4} color="severity.success" />
                  </Box>
                  <FeatureLabel>{feature.label}</FeatureLabel>
                </HStack>
              ))}
            </FeatureList>
          </FeatureGroup>
        ))}
      </FeatureGrid>
    </SectionCard>
  );
}

const SectionHeading = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${({theme}) => theme.getSize(4)};
  padding: ${({theme}) => theme.getSize(5)} ${({theme}) => theme.getSize(6)} 0;
`;
const FeatureCount = styled.span`
  color: ${({theme}) => theme.colors.text.secondary};
  background: ${({theme}) => theme.colors.general.separator};
  border-radius: ${({theme}) => theme.radii.medium};
  padding: ${({theme}) => theme.getSize(1)} ${({theme}) => theme.getSize(2)};
`;
const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: ${({theme}) => theme.getSize(7)};
  padding: ${({theme}) => theme.getSize(5)} ${({theme}) => theme.getSize(6)}
    ${({theme}) => theme.getSize(6)};
  @container preview (max-width: ${getPreviewContainerBreakpoint(
    PREVIEW_CONTAINER_BREAKPOINTS.wide
  )}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @container preview (max-width: ${getPreviewContainerBreakpoint(
    PREVIEW_CONTAINER_BREAKPOINTS.phone
  )}) {
    grid-template-columns: minmax(0, 1fr);
  }
`;
const FeatureGroup = styled.section`
  min-width: 0;
`;
const GroupHeading = styled.div`
  padding-bottom: ${({theme}) => theme.getSize(2)};
  border-bottom: 1px solid ${({theme}) => theme.colors.general.separator};
`;
const FeatureList = styled.div`
  display: grid;
  gap: ${({theme}) => theme.getSize(2)};
  padding: ${({theme}) => theme.getSize(3)} 0 0;
`;
const FeatureLabel = styled.span`
  overflow-wrap: anywhere;
`;
